import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnInit } from '@angular/core';
import { Details } from '@core/dataiku-api/definitions/private-feature-store-api';
import { NavItems } from '@core/nav/nav-items.enum';
import { NavTabs } from '@core/nav/nav-tabs.enum';
import { NavUniverses } from '@core/nav/nav-universes.enum';
import { TopNavService } from '@core/nav/top-nav.service';
import { WaitingService } from '@core/overlays/waiting.service';
import { fairAny } from 'dku-frontend-core';
import _ from 'lodash';
import { forkJoin, of, switchMap } from 'rxjs';
import { FacetMap, SearchOptions } from './feature-store-models';
import { FeatureStoreService } from './feature-store.service';
import { Aggregation, Hit } from './query-result-models';

@Component({
  selector: 'feature-store',
  templateUrl: './feature-store.component.html',
  styleUrls: ['./feature-store.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FeatureStoreComponent implements OnInit {

  @Input() projectKey: string | undefined;

  users: Record<string, string>;
  projectNames: Record<string, string>;
  tagsMap: any;
  searchQuery: string;
  sortedBy: string;
  reverseOrder: boolean;
  activeFacets: FacetMap;
  hits: Hit[];
  aggregationResults: Record<string, Aggregation>;
  selectedItem: Hit | null;
  details: Details;
  datasetsInCurrentProject: string[];
  disableImmediateFlush: boolean = false;

  constructor(
    @Inject("TaggingService") private taggingService: fairAny,
    @Inject('StateUtils') private stateUtils: fairAny,
    @Inject('$rootScope') public $rootScope: fairAny,
    @Inject("ActivityIndicator") private activityIndicator: fairAny,
    public featureStoreService: FeatureStoreService,
    private changeDetectorRef: ChangeDetectorRef,
    private topNav: TopNavService,
    private waitingService: WaitingService,
  ) {
    this.users = {};
    this.projectNames = {};
    this.searchQuery = "";
    this.sortedBy = "score";
    this.reverseOrder = true;
    this.activeFacets = {};
    this.aggregationResults = {};
  }

  ngOnInit(): void {
    if(this.projectKey) {
      this.topNav.setLocation(NavUniverses.FLOW, NavItems.DATASET, NavTabs.NONE);
    } else {
      this.topNav.setLocation(NavUniverses.DSS_HOME, NavItems.FEATURE_STORE);
    }
    this.topNav.setNoItem();
    this.disableImmediateFlush = this.$rootScope.appConfig.disableImmediateFlush;
    this.taggingService.fetchGlobalTags();

    forkJoin([
      this.featureStoreService.listUsers(),
      this.featureStoreService.listProjects(),
      this.featureStoreService.listTags(),
      this.projectKey?this.featureStoreService.listAccessibleDatasetsInProject(this.projectKey):of([])
    ])
      .subscribe(([usersResponse, projectsResponse, tagsResponse, accessiblesResponse]) => {
        this.datasetsInCurrentProject = accessiblesResponse.map(d => d.projectKey + "." + d.id);
        usersResponse.forEach((user: any) => {
          this.users[user.login] = user.displayName;
        })
        projectsResponse.forEach(project => {
          this.projectNames[project.projectKey] = project.name;
        })
        this.tagsMap = tagsResponse;
        this.search();
      });
  }

  search(): void {
    this.featureStoreService.search(this.searchQuery, this.activeFacets)
      .subscribe(results => {
        this.selectedItem = results.hits.hits.find(h => h._id === this.selectedItem?._id) || null;
        this.hits = this.sortHits(results.hits.hits);
        this.aggregationResults = results.aggregations;
        this.changeDetectorRef.markForCheck();
      });
  }

  sortHits(hits: Hit[]): Hit[] {
    if (!hits) {
      return hits;
    } else {
      const getSortAttribute = (h: Hit) => {
        if (this.sortedBy === "createdOn") return h._source.createdOn;
        else if (this.sortedBy === "lastModifiedOn") return h._source.lastModifiedOn;
        else return h._score;
      }
      // using slice() to change the array reference
      const sorted = hits.slice().sort((a: Hit, b: Hit) => getSortAttribute(b) - getSortAttribute(a))
      return this.reverseOrder || this.sortedBy === "score" ? sorted : sorted.reverse();
    }
  }

  newQuery(searchOptions: SearchOptions) {
    this.sortedBy = searchOptions.sortBy;
    this.reverseOrder = searchOptions.reverse;
    if (searchOptions.query !== this.searchQuery) {
      this.searchQuery = searchOptions.query;
      this.search();
    } else {
      this.hits = this.sortHits(this.hits);
      this.changeDetectorRef.markForCheck();
    }
  }

  newFacetSelection(facetMap: FacetMap) {
    if (!_.isEqual(facetMap, this.activeFacets)) {
      this.activeFacets = facetMap;
      this.search();
    }
  }

  newItemSelected(item: Hit): void {
    this.featureStoreService.getDetails(
      item._source.projectKey,
      item._source.id
    ).subscribe((details) => {
      this.details = details;
      this.selectedItem = item;
      this.changeDetectorRef.markForCheck();
    });
  }

  resetFiltersRequest(): void {
    this.searchQuery = "";
    this.activeFacets = {};
    this.search();
  }

  removedFromFeatureStore(item: Hit): void {
    this.featureStoreService.removeFromFeatureStore(item)
      .pipe(
        switchMap(() => {
          if(this.disableImmediateFlush) {
            this.activityIndicator.warning("Your removal request is being processed. It may take a few seconds to be reflected here.", 10000);
            return of();
          }
          else return this.featureStoreService.flush();
        }),
        this.waitingService.bindSpinner(),
      )
      .subscribe(() => this.search());
  }

  currentProjectLink(): void {
    return this.stateUtils.href.project(
      this.projectKey
    );
  }
}
